import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Wallet, CreditCard, Loader2, ShieldCheck, AlertTriangle, IndianRupee, CheckCircle2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";

/**
 * Sprint 80 — Customer-driven "Pay Difference" dialog.
 *
 * Powers the 1-tap unblock flow for orders sitting on a post-edit
 * upcharge (pending_settlement). Two payment paths:
 *   1. Wallet  — atomic debit if balance ≥ amount (instant).
 *   2. Razorpay — opens checkout, verifies signature, marks paid.
 *
 * Props
 *   open               : bool, controlled
 *   onOpenChange       : (bool) => void
 *   trackingId         : string
 *   onPaid             : (freshOrder) => void   — fires after successful payment
 */
export default function PayDifferenceDialog({ open, onOpenChange, trackingId, onPaid }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // "wallet" | "razorpay" | null
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(null); // success payload

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await api.get(`/orders/${trackingId}/pending-settlement`);
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load pending settlement");
    } finally {
      setLoading(false);
    }
  }, [trackingId]);

  useEffect(() => {
    if (!open || !trackingId) return;
    setDone(null);
    fetchSummary();
  }, [open, trackingId, fetchSummary]);

  const payWithWallet = async () => {
    setBusy("wallet");
    setErr(null);
    try {
      const res = await api.post(`/orders/${trackingId}/pay-settlement/wallet`);
      toast.success(res.data?.message || "Paid from wallet");
      setDone(res.data);
      if (onPaid) onPaid(res.data?.order);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Wallet payment failed";
      setErr(detail);
      toast.error(detail);
    } finally {
      setBusy(null);
    }
  };

  const ensureRazorpayScript = () =>
    new Promise((resolve, reject) => {
      if (window.Razorpay) return resolve();
      const id = "razorpay-script";
      if (document.getElementById(id)) {
        // Script tag exists but not loaded — wait a tick.
        const wait = setInterval(() => {
          if (window.Razorpay) {
            clearInterval(wait);
            resolve();
          }
        }, 100);
        setTimeout(() => {
          clearInterval(wait);
          reject(new Error("Razorpay script took too long to load"));
        }, 8000);
        return;
      }
      const script = document.createElement("script");
      script.id = id;
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Razorpay"));
      document.body.appendChild(script);
    });

  const payWithRazorpay = async () => {
    setBusy("razorpay");
    setErr(null);
    try {
      const { data: rz } = await api.post(`/orders/${trackingId}/pay-settlement/razorpay-order`);
      if (!rz?.razorpay_order_id) {
        throw new Error("Could not start Razorpay payment");
      }

      // Sprint-105b — Mock short-circuit (mirrors Sprint-97 booking flow).
      // When backend is in MOCK_RAZORPAY mode it returns `mock: true` and
      // `key_id: "rzp_test_mock"`. Skip the real Razorpay JS SDK and call
      // verify directly with the canonical MOCK_SIGNATURE — the backend
      // accepts it only for mock-prefixed intents.
      if (rz.mock || rz.key_id === "rzp_test_mock") {
        try {
          const { data: verify } = await api.post(
            `/orders/${trackingId}/pay-settlement/razorpay-verify`,
            {
              razorpay_order_id: rz.razorpay_order_id,
              razorpay_payment_id: `pay_mock_${rz.razorpay_order_id.slice(-8)}`,
              razorpay_signature: "MOCK_SIGNATURE",
            },
          );
          toast.success(verify.message || "Payment verified");
          setDone(verify);
          if (onPaid) onPaid(verify?.order);
        } catch (e) {
          const detail = e?.response?.data?.detail || e?.message || "Could not verify payment";
          setErr(detail);
          toast.error(detail);
        } finally {
          setBusy(null);
        }
        return;
      }

      if (!rz.key_id) {
        throw new Error("Razorpay not configured on the server");
      }
      await ensureRazorpayScript();

      // eslint-disable-next-line no-undef
      const rzp = new window.Razorpay({
        key: rz.key_id,
        amount: rz.amount,
        currency: rz.currency || "INR",
        name: "PickNGo",
        description: `Edit upcharge for ${trackingId}`,
        order_id: rz.razorpay_order_id,
        handler: async (response) => {
          try {
            const verify = await api.post(
              `/orders/${trackingId}/pay-settlement/razorpay-verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
            );
            toast.success(verify.data?.message || "Payment verified");
            setDone(verify.data);
            if (onPaid) onPaid(verify.data?.order);
          } catch (e) {
            const detail = e?.response?.data?.detail || "Could not verify payment";
            setErr(detail);
            toast.error(detail);
          } finally {
            setBusy(null);
          }
        },
        modal: {
          ondismiss: () => setBusy(null),
        },
        theme: { color: "#FF6B00" },
      });
      rzp.on("payment.failed", (resp) => {
        const reason = resp?.error?.description || "Payment failed";
        setErr(reason);
        toast.error(reason);
        setBusy(null);
      });
      rzp.open();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Could not start payment";
      setErr(detail);
      toast.error(detail);
      setBusy(null);
    }
  };

  const amount = data?.pending_amount_inr || 0;
  const walletBalance = data?.wallet_balance_inr || 0;
  const walletSufficient = !!data?.wallet_sufficient;
  const razorpayEnabled = !!data?.razorpay_enabled;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="pay-difference-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-xl font-bold flex items-center gap-2">
            <IndianRupee className="h-5 w-5 text-[#FF6B00]" />
            Pay difference
          </DialogTitle>
          <DialogDescription>
            Settle the additional amount from your recent order edit to unblock this shipment.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-10" data-testid="pay-difference-loading">
            <Loader2 className="h-5 w-5 animate-spin text-[#FF6B00]" />
            <span className="ml-3 text-sm text-slate-500">Loading…</span>
          </div>
        )}

        {!loading && done && (
          <div
            className="p-5 rounded-xl bg-emerald-50 border border-emerald-200 text-center"
            data-testid="pay-difference-success"
          >
            <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
            <p className="font-bold text-emerald-800">{done.message}</p>
            <p className="text-xs text-emerald-700 mt-1">
              Ref: <span className="font-mono">{done.transaction_ref}</span>
            </p>
            <Button
              onClick={() => onOpenChange(false)}
              className="mt-4 bg-emerald-600 text-white hover:bg-emerald-700 rounded-xl"
              data-testid="pay-difference-done-btn"
            >
              Done
            </Button>
          </div>
        )}

        {!loading && !done && data && (
          <>
            {amount <= 0 ? (
              <div
                className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-sm text-emerald-700 flex items-center gap-2"
                data-testid="pay-difference-no-pending"
              >
                <CheckCircle2 className="h-4 w-4" />
                Nothing to pay — your order is already unblocked.
              </div>
            ) : (
              <>
                {/* Amount header */}
                <div
                  className="rounded-2xl border border-amber-200 bg-amber-50 p-4"
                  data-testid="pay-difference-amount-card"
                >
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-700">
                    Amount due
                  </p>
                  <p className="text-3xl font-extrabold text-amber-900" data-testid="pay-difference-amount">
                    ₹{amount}
                  </p>
                  {(data.pending_rows || [])
                    .filter((r) => r.reason)
                    .slice(0, 2)
                    .map((r, idx) => (
                      <p key={`reason-${idx}`} className="text-xs text-amber-700 mt-1">
                        {r.reason === "address_change" ? "Reason: Address change" :
                          r.reason === "edit_reprice" ? "Reason: Order edit reprice" :
                          `Reason: ${r.reason}`}
                      </p>
                    ))}
                </div>

                {err && (
                  <div
                    className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-start gap-2"
                    data-testid="pay-difference-error"
                  >
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{err}</span>
                  </div>
                )}

                {/* Wallet option */}
                <button
                  type="button"
                  onClick={payWithWallet}
                  disabled={!walletSufficient || busy !== null}
                  className={`w-full rounded-2xl border-2 p-4 flex items-center justify-between gap-3 text-left transition ${
                    walletSufficient
                      ? "border-emerald-200 bg-emerald-50 hover:border-emerald-400 hover:shadow-sm"
                      : "border-slate-200 bg-slate-50 cursor-not-allowed"
                  }`}
                  data-testid="pay-difference-wallet-btn"
                >
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${
                      walletSufficient ? "bg-emerald-100" : "bg-slate-200"
                    }`}>
                      <Wallet className={`h-5 w-5 ${walletSufficient ? "text-emerald-700" : "text-slate-500"}`} />
                    </div>
                    <div>
                      <p className="font-bold text-slate-900">PickNGo Wallet</p>
                      <p className="text-xs text-slate-600">
                        Balance: <span className="font-semibold">₹{walletBalance}</span>
                        {!walletSufficient && amount > 0 && (
                          <span className="ml-1 text-red-600 font-semibold" data-testid="pay-difference-wallet-short">
                            — insufficient balance (short by ₹{amount - walletBalance})
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  {busy === "wallet" ? (
                    <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
                  ) : walletSufficient ? (
                    <span className="text-xs font-bold text-emerald-700">Pay ₹{amount}</span>
                  ) : (
                    <span className="text-xs font-semibold text-slate-400">Unavailable</span>
                  )}
                </button>

                {/* Top-up wallet — shown only when wallet is short */}
                {!walletSufficient && amount > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      onOpenChange(false);
                      navigate("/dashboard/wallet");
                    }}
                    className="w-full -mt-1.5 inline-flex items-center justify-between gap-2 px-4 py-2 rounded-xl bg-emerald-50/60 hover:bg-emerald-100 border border-dashed border-emerald-300 text-emerald-800 text-xs font-semibold transition"
                    data-testid="pay-difference-wallet-topup-btn"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <ArrowUpRight className="h-3.5 w-3.5" />
                      Top up wallet to pay instantly
                    </span>
                    <span className="text-[10px] text-emerald-600">/dashboard/wallet</span>
                  </button>
                )}

                {/* Razorpay option */}
                <button
                  type="button"
                  onClick={payWithRazorpay}
                  disabled={!razorpayEnabled || busy !== null}
                  className={`w-full rounded-2xl border-2 p-4 flex items-center justify-between gap-3 text-left transition ${
                    razorpayEnabled
                      ? "border-orange-200 bg-orange-50/50 hover:border-orange-400 hover:shadow-sm"
                      : "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed"
                  }`}
                  data-testid="pay-difference-razorpay-btn"
                >
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${
                      razorpayEnabled ? "bg-orange-100" : "bg-slate-200"
                    }`}>
                      <CreditCard className={`h-5 w-5 ${razorpayEnabled ? "text-[#FF6B00]" : "text-slate-500"}`} />
                    </div>
                    <div>
                      <p className="font-bold text-slate-900">Pay online</p>
                      <p className="text-xs text-slate-600">
                        UPI · Cards · Net banking · Razorpay
                        {!razorpayEnabled && (
                          <span className="ml-1 text-slate-500">— not configured on this server</span>
                        )}
                      </p>
                    </div>
                  </div>
                  {busy === "razorpay" ? (
                    <Loader2 className="h-5 w-5 animate-spin text-[#FF6B00]" />
                  ) : (
                    <span className="text-xs font-bold text-[#FF6B00]">Pay ₹{amount} →</span>
                  )}
                </button>

                <div className="text-[11px] text-slate-500 flex items-center gap-1.5 mt-1">
                  <ShieldCheck className="h-3 w-3" />
                  Payments are processed securely. Order auto-unblocks after settlement.
                </div>
              </>
            )}
          </>
        )}

        {!loading && err && !data && (
          <div className="p-4 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
            {err}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
